package fr.manyverse.app.presentation.viewmodel

import androidx.lifecycle.viewModelScope
import fr.manyverse.app.AppLogger
import fr.manyverse.app.domain.model.AudioData
import fr.manyverse.app.domain.model.Sound
import fr.manyverse.app.domain.model.SoundTag
import fr.manyverse.app.domain.model.SoundType
import fr.manyverse.app.domain.repository.SoundRepository
import fr.manyverse.app.domain.usecase.DeleteSoundUseCase
import fr.manyverse.app.domain.usecase.GetUserSoundsUseCase
import fr.manyverse.app.domain.usecase.UploadSoundUseCase
import fr.manyverse.app.domain.util.SoundFilter
import fr.manyverse.app.infrastructure.audio.AmbientPlaybackState
import fr.manyverse.app.infrastructure.audio.CrossfadeAudioPlayer
import fr.manyverse.app.infrastructure.audio.CrossfadePlaybackState
import fr.manyverse.app.presentation.error.UserErrorNotifier
import fr.manyverse.app.presentation.error.toUserMessage
import fr.manyverse.app.presentation.state.AsyncUiState
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

private const val TAG = "SoundPlayerViewModel"

/**
 * UI state for the sound player.
 */
data class SoundPlayerUiState(
    val sounds: List<Sound> = emptyList(),
    val selectedTypes: Set<SoundType> = emptySet(),
    val selectedTags: Set<SoundTag> = emptySet(),
    val searchQuery: String = "",
    val playbackState: CrossfadePlaybackState = CrossfadePlaybackState(),
    val soundVolumes: Map<String, Float> = emptyMap(),
    val currentSound: Sound? = null,
    val showAddDialog: Boolean = false,
    val selectedAudioData: AudioData? = null,
    val uploadState: AsyncUiState<Sound> = AsyncUiState.Idle,
    val deleteState: AsyncUiState<Unit> = AsyncUiState.Idle,
    val activeAmbientSounds: Set<String> = emptySet(),
    val masterVolume: Float = 1.0f,
    val autoPlayTag: SoundTag? = null
)

/**
 * ViewModel for managing sound playback in the Sounds tab.
 */
class SoundPlayerViewModel(
    private val soundRepository: SoundRepository,
    private val audioPlayer: CrossfadeAudioPlayer,
    private val universeId: String,
    private val uploadSoundUseCase: UploadSoundUseCase,
    private val deleteSoundUseCase: DeleteSoundUseCase,
    private val getUserSoundsUseCase: GetUserSoundsUseCase,
    private val currentUserId: String,
    private val errorNotifier: UserErrorNotifier
) : BaseViewModel() {

    private val _selectedTypes = MutableStateFlow<Set<SoundType>>(setOf(SoundType.MUSIC))
    private val _masterVolume = MutableStateFlow(1.0f)
    private val _selectedTags = MutableStateFlow<Set<SoundTag>>(emptySet())
    private val _soundVolumes = MutableStateFlow<Map<String, Float>>(emptyMap())
    private val _searchQuery = MutableStateFlow("")
    private val _allSounds = MutableStateFlow<List<Sound>>(emptyList())
    private val _showAddDialog = MutableStateFlow(false)
    private val _selectedAudioData = MutableStateFlow<AudioData?>(null)
    private val _uploadState = MutableStateFlow<AsyncUiState<Sound>>(AsyncUiState.Idle)
    private val _deleteState = MutableStateFlow<AsyncUiState<Unit>>(AsyncUiState.Idle)
    private val _autoPlayTag = MutableStateFlow<SoundTag?>(null)

    // Event emitted when a sound is played via random selection (for auto-scroll)
    private val _randomSoundPlayed = MutableSharedFlow<String>()
    val randomSoundPlayed: SharedFlow<String> = _randomSoundPlayed.asSharedFlow()

    val uiState: StateFlow<SoundPlayerUiState> = combine(
        _selectedTypes,
        _selectedTags,
        _searchQuery,
        audioPlayer.state,
        _soundVolumes,
        _allSounds,
        _showAddDialog,
        _selectedAudioData,
        _uploadState,
        _deleteState,
        audioPlayer.ambientState,
        _masterVolume,
        _autoPlayTag
    ) { array ->
        @Suppress("UNCHECKED_CAST")
        val tags = array[1] as Set<SoundTag>

        @Suppress("UNCHECKED_CAST")
        val types = array[0] as Set<SoundType>
        val searchQuery = array[2] as String
        val playback = array[3] as CrossfadePlaybackState

        @Suppress("UNCHECKED_CAST")
        val volumes = array[4] as Map<String, Float>

        @Suppress("UNCHECKED_CAST")
        val allSounds = array[5] as List<Sound>
        val showAddDialog = array[6] as Boolean
        val selectedAudioData = array[7] as AudioData?

        @Suppress("UNCHECKED_CAST")
        val uploadState = array[8] as AsyncUiState<Sound>

        @Suppress("UNCHECKED_CAST")
        val deleteState = array[9] as AsyncUiState<Unit>
        val ambientState = array[10] as AmbientPlaybackState
        val masterVolume = array[11] as Float
        val autoPlayTag = array[12] as SoundTag?

        val sortedSounds = SoundFilter.filterAndSort(allSounds, types, tags, searchQuery)

        // Find current sound from all sounds (not filtered) to keep overlay visible
        val currentSound = playback.currentSoundId?.let { id ->
            allSounds.find { it.id == id }
        }

        SoundPlayerUiState(
            sounds = sortedSounds,
            selectedTypes = types,
            selectedTags = tags,
            searchQuery = searchQuery,
            playbackState = playback,
            soundVolumes = volumes,
            currentSound = currentSound,
            showAddDialog = showAddDialog,
            selectedAudioData = selectedAudioData,
            uploadState = uploadState,
            deleteState = deleteState,
            activeAmbientSounds = ambientState.activeSounds,
            masterVolume = masterVolume,
            autoPlayTag = autoPlayTag
        )
    }.stateIn(
        scope = viewModelScope,
        started = SharingStarted.WhileSubscribed(5000),
        initialValue = SoundPlayerUiState()
    )

    init {
        loadSounds()
        viewModelScope.launch {
            audioPlayer.trackCompleted.collect { completedSoundId ->
                val tag = _autoPlayTag.value
                if (tag != null) {
                    playRandomMusicByTag(tag, excludeSoundId = completedSoundId)
                } else {
                    // Auto-play was deactivated mid-play: loop the track that just finished
                    val sound = _allSounds.value.find { it.id == completedSoundId }
                    sound?.let { playSound(it) }
                }
            }
        }
    }

    /**
     * Loads sounds including user-uploaded sounds.
     */
    fun loadSounds() {
        viewModelScope.launch {
            getUserSoundsUseCase(universeId, currentUserId)
                .onSuccess { sounds ->
                    _allSounds.value = sounds
                }
                .onFailure { error ->
                    // Les sons par défaut restent jouables, mais le MJ doit savoir que les siens
                    // manquent plutôt que de les croire supprimés
                    _allSounds.value = soundRepository.getSoundsByUniverse(universeId)
                    errorNotifier.notify(error)
                }
        }
    }

    /**
     * Selects a single sound type tab, replacing any current type filter.
     */
    fun selectTypeTab(type: SoundType) {
        _selectedTypes.value = setOf(type)
    }

    /**
     * Toggles a tag filter on or off.
     */
    fun toggleTagFilter(tag: SoundTag) {
        val current = _selectedTags.value
        _selectedTags.value = if (tag in current) {
            current - tag
        } else {
            current + tag
        }
    }

    /**
     * Updates the search query for filtering sounds by name.
     */
    fun updateSearchQuery(query: String) {
        _searchQuery.value = query
    }

    /**
     * Shows the add sound dialog.
     */
    fun showAddDialog() {
        _showAddDialog.value = true
        _uploadState.value = AsyncUiState.Idle
    }

    /**
     * Hides the add sound dialog and clears form data.
     */
    fun hideAddDialog() {
        _showAddDialog.value = false
        _selectedAudioData.value = null
        _uploadState.value = AsyncUiState.Idle
    }

    /**
     * Sets the selected audio data from the file picker.
     */
    fun selectAudioData(audioData: AudioData?) {
        _selectedAudioData.value = audioData
    }

    /**
     * Uploads a new sound.
     */
    fun uploadSound(name: String, type: SoundType, tags: Set<SoundTag>) {
        val audioData = _selectedAudioData.value ?: return

        viewModelScope.launch {
            _uploadState.value = AsyncUiState.Loading

            uploadSoundUseCase(
                userId = currentUserId,
                universeId = universeId,
                name = name,
                type = type,
                tags = tags,
                audioData = audioData
            ).onSuccess { sound ->
                _uploadState.value = AsyncUiState.Success(sound)
                // Reload sounds to include the new one
                loadSounds()
                // Close dialog
                hideAddDialog()
            }.onFailure { error ->
                // Les échecs d'enregistrement (avec cause) sont déjà remontés par le repository :
                // seuls les refus de validation, sans cause, restaient invisibles
                if (error.cause == null) {
                    AppLogger.error(
                        TAG,
                        "Sound upload rejected (${audioData.mimeType}, ${audioData.bytes.size} bytes)",
                        error
                    )
                }
                _uploadState.value = AsyncUiState.Error(error.toUserMessage())
            }
        }
    }

    /**
     * Deletes a user-uploaded sound.
     */
    fun deleteSound(soundId: String) {
        viewModelScope.launch {
            _deleteState.value = AsyncUiState.Loading

            deleteSoundUseCase(soundId, currentUserId)
                .onSuccess {
                    _deleteState.value = AsyncUiState.Success(Unit)

                    // Stop playback if this sound is currently playing as music
                    if (audioPlayer.state.value.currentSoundId == soundId) {
                        audioPlayer.stop()
                    }

                    // Stop ambient playback if this sound is currently playing as ambient
                    if (audioPlayer.isAmbientPlaying(soundId)) {
                        audioPlayer.stopAmbient(soundId)
                    }

                    // Reload sounds to remove the deleted one
                    loadSounds()
                }
                .onFailure { error ->
                    _deleteState.value = AsyncUiState.Error(error.toUserMessage())
                }
        }
    }

    /**
     * Checks if a sound can be deleted by the current user.
     */
    fun canDeleteSound(sound: Sound): Boolean {
        return sound.isUserUploaded && sound.userId == currentUserId
    }

    /**
     * Plays a sound. For MUSIC, uses crossfade. For EFFECT, plays once without affecting music.
     * For AMBIENT, toggles the sound on/off.
     */
    fun playSound(sound: Sound) {
        viewModelScope.launch {
            val volume = _soundVolumes.value[sound.id] ?: 1.0f
            val effectiveVolume = volume * _masterVolume.value

            if (sound.type == SoundType.MUSIC) {
                val currentTag = _autoPlayTag.value
                if (currentTag != null && !sound.tags.contains(currentTag)) {
                    _autoPlayTag.value = null
                }
            }

            val musicLoop = _autoPlayTag.value == null

            if (sound.isUserUploaded && sound.storagePath != null) {
                // User-uploaded sound: get bytes from repository
                soundRepository.getAudioBytes(sound)
                    .onSuccess { bytes ->
                        when (sound.type) {
                            SoundType.EFFECT -> {
                                audioPlayer.playEffectFromBytes(bytes, sound.id, effectiveVolume)
                            }

                            SoundType.MUSIC -> {
                                audioPlayer.playFromBytes(bytes, sound.id, loop = musicLoop)
                                audioPlayer.setVolume(effectiveVolume)
                            }

                            SoundType.AMBIENT -> {
                                // Toggle: if active -> stop, otherwise -> play
                                if (audioPlayer.isAmbientPlaying(sound.id)) {
                                    audioPlayer.stopAmbient(sound.id)
                                } else {
                                    audioPlayer.playAmbientFromBytes(bytes, sound.id, effectiveVolume)
                                }
                            }
                        }
                    }
            } else {
                // Default sound: load from resources
                val resourcePath = "files/audio/${sound.fileName}"

                when (sound.type) {
                    SoundType.EFFECT -> {
                        audioPlayer.playEffect(resourcePath, sound.id, effectiveVolume)
                    }

                    SoundType.MUSIC -> {
                        audioPlayer.playWithCrossfade(resourcePath, sound.id, loop = musicLoop)
                        audioPlayer.setVolume(effectiveVolume)
                    }

                    SoundType.AMBIENT -> {
                        // Toggle: if active -> stop, otherwise -> play
                        if (audioPlayer.isAmbientPlaying(sound.id)) {
                            audioPlayer.stopAmbient(sound.id)
                        } else {
                            audioPlayer.playAmbient(resourcePath, sound.id, effectiveVolume)
                        }
                    }
                }
            }
        }
    }

    /**
     * Stops all playback (music + all ambient sounds + any playing effect).
     * Called when the screen is stopped (navigation or app background).
     */
    fun stopAll() {
        audioPlayer.stop()
        audioPlayer.stopAllAmbients()
        audioPlayer.stopEffect()
    }

    /**
     * Stops the current playback and deactivates auto-play.
     */
    fun stopSound() {
        _autoPlayTag.value = null
        audioPlayer.stop()
    }

    /**
     * Toggles between play and pause for the current sound.
     */
    fun togglePlayPause() {
        val state = audioPlayer.state.value
        if (state.isPlaying) {
            audioPlayer.pause()
        } else if (state.currentSoundId != null) {
            audioPlayer.resume()
        }
    }

    /**
     * Sets the volume for a specific sound.
     */
    fun setSoundVolume(soundId: String, volume: Float) {
        val safeVolume = volume.coerceIn(0f, 1f)
        _soundVolumes.value += (soundId to safeVolume)
        val masterVol = _masterVolume.value

        // If this sound is currently playing as music, apply immediately
        if (audioPlayer.state.value.currentSoundId == soundId) {
            audioPlayer.setVolume(safeVolume * masterVol)
        }

        // If this sound is currently playing as ambient, apply immediately
        if (audioPlayer.isAmbientPlaying(soundId)) {
            audioPlayer.setAmbientVolume(soundId, safeVolume * masterVol)
        }
    }

    /**
     * Sets the master volume, scaling all currently playing sounds.
     */
    fun setMasterVolume(volume: Float) {
        val safe = volume.coerceIn(0f, 1f)
        _masterVolume.value = safe

        // Update currently playing music
        val currentSoundId = audioPlayer.state.value.currentSoundId
        if (currentSoundId != null) {
            val soundVol = _soundVolumes.value[currentSoundId] ?: 1.0f
            audioPlayer.setVolume(soundVol * safe)
        }

        // Update all active ambients
        audioPlayer.ambientState.value.activeSounds.forEach { soundId ->
            val soundVol = _soundVolumes.value[soundId] ?: 1.0f
            audioPlayer.setAmbientVolume(soundId, soundVol * safe)
        }
    }

    /**
     * Gets the volume for a specific sound.
     */
    fun getSoundVolume(soundId: String): Float {
        return _soundVolumes.value[soundId] ?: 1.0f
    }

    /**
     * Toggles auto-play for the given ambiance tag.
     * If the tag is already active, deactivates auto-play.
     * If a different or no tag was active, activates with the new tag.
     * Starts playing immediately if no track is currently playing.
     */
    fun selectAutoPlayTag(tag: SoundTag) {
        val current = _autoPlayTag.value
        if (current == tag) {
            _autoPlayTag.value = null
        } else {
            _autoPlayTag.value = tag
            // Always start a track from the chosen ambiance.
            // If something is already playing (possibly looping), crossfade to it immediately
            // so the new track plays with loop=false and auto-play can chain correctly.
            playRandomMusicByTag(tag)
        }
    }

    /**
     * Deactivates auto-play. The current track continues playing to its natural end,
     * then the trackCompleted collector will replay it in loop mode.
     */
    fun deactivateAutoPlay() {
        _autoPlayTag.value = null
    }

    /**
     * Skips to the next random track matching the active auto-play tag.
     * Does nothing if auto-play is not active.
     */
    fun playNextTrack() {
        val tag = _autoPlayTag.value ?: return
        playRandomMusicByTag(tag)
    }

    /**
     * Plays a random music track matching the specified tag.
     * Excludes [excludeSoundId] when provided, otherwise excludes the currently playing track.
     */
    fun playRandomMusicByTag(tag: SoundTag, excludeSoundId: String? = null) {
        viewModelScope.launch {
            val currentSoundId = audioPlayer.state.value.currentSoundId
            val idToExclude = excludeSoundId ?: currentSoundId

            val candidateSounds = _allSounds.value.filter { sound ->
                sound.type == SoundType.MUSIC && tag in sound.tags
            }

            val availableSounds = if (idToExclude != null) {
                candidateSounds.filter { it.id != idToExclude }
            } else {
                candidateSounds
            }

            availableSounds.randomOrNull()?.let { sound ->
                playSound(sound)
                _randomSoundPlayed.emit(sound.id)
            }
        }
    }

    override fun onCleared() {
        super.onCleared()
        audioPlayer.reset()
    }
}
